import { useMutation } from '@apollo/client';
import {
  IError,
  IRoleDisableMutation,
  IRoleDisableMutationVariables,
  IRoleEnableMutation,
  IRoleEnableMutationVariables,
  IRoleWithPermissionsFieldsFragment,
  IUserEnableMutation,
  IUserEnableMutationVariables,
  IUserGroupDisableMutation,
  IUserGroupDisableMutationVariables,
  IUserGroupEnableMutation,
  IUserGroupEnableMutationVariables,
  IUserGroupFieldsFragment,
  IUserPermissionFieldsFragment,
} from '../../../utils/apollo/graphqlSchema';
import {
  ROLE_DISABLE,
  ROLE_ENABLE,
  USER_ENABLE,
  USER_GROUP_DISABLE,
  USER_GROUP_ENABLE,
} from '../../../utils/apollo/permission';
import { assertCondition } from '../../../utils/types';
import { useCallback, useMemo } from 'react';
import printGraphqlErrors from '../../../utils/apollo/printGraphqlErrors';
import toastMessage from '../../../utils/toastMessage';
import { UnreachableCaseError } from 'ts-essentials';

interface IProps {
  entity:
    | IUserPermissionFieldsFragment
    | IUserGroupFieldsFragment
    | IRoleWithPermissionsFieldsFragment;
  buttonType: 'enableButton' | 'disableButton';
}
export const allDictionary = {
  user: {
    disableButton: {
      name: 'Disable User',
      description:
        "The disabled user won't be able to login. All reports, cohorts and dashboards will remain the same.",
      trackContext: 'users',
      trackAction: 'disable user',
      trackToggleConfirmAction: 'disable user - confirm',
      trackToggleCancelAction: 'disable user - cancel',
    },
    enableButton: {
      name: 'Enable User',
      description: 'The enabled user will be able to login again.',
      trackContext: 'users',
      trackAction: 'enable user',
      trackToggleConfirmAction: 'enable user - confirm',
      trackToggleCancelAction: 'enable user - cancel',
    },
  },
  userGroup: {
    disableButton: {
      name: 'Disable Group',
      description: "Disabled group can't be edited or assigned to users.",
      trackContext: 'groups',
      trackAction: 'disable group',
      trackToggleConfirmAction: 'disable group - confirm',
      trackToggleCancelAction: 'disable group - cancel',
    },
    enableButton: {
      name: 'Enable Group',
      description: 'Enable this group to be editable and assignable to users.',
      trackContext: 'groups',
      trackAction: 'enable group',
      trackToggleConfirmAction: 'enable group - confirm',
      trackToggleCancelAction: 'enable group - cancel',
    },
  },
  role: {
    disableButton: {
      name: 'Disable Role',
      description: "Disabled role can't be edited or assigned.",
      trackContext: 'roles',
      trackAction: 'disable role',
      trackToggleConfirmAction: 'disable role - confirm',
      trackToggleCancelAction: 'disable role - cancel',
    },
    enableButton: {
      name: 'Enable Role',
      description: 'Enable this role to be editable and assignable to users.',
      trackContext: 'roles',
      trackAction: 'enable role',
      trackToggleConfirmAction: 'enable role - confirm',
      trackToggleCancelAction: 'enable role - cancel',
    },
  },
};

/**
 * This hook is used to toggle the permission of a user, user group or role.
 */
const usePermissionToggleMutation = ({ entity, buttonType }: IProps) => {
  const textDictionary = useMemo(() => {
    if (entity.__typename === 'User') {
      return allDictionary.user[buttonType];
    } else if (entity.__typename === 'UserGroup') {
      return allDictionary.userGroup[buttonType];
    } else if (entity.__typename === 'Role') {
      return allDictionary.role[buttonType];
    } else {
      throw new UnreachableCaseError(entity);
    }
  }, [buttonType, entity]);
  const [disableRole] = useMutation<
    IRoleDisableMutation,
    IRoleDisableMutationVariables
  >(ROLE_DISABLE);
  const [enableRole] = useMutation<
    IRoleEnableMutation,
    IRoleEnableMutationVariables
  >(ROLE_ENABLE);

  const [disableGroup] = useMutation<
    IUserGroupDisableMutation,
    IUserGroupDisableMutationVariables
  >(USER_GROUP_DISABLE);
  const [enableGroup] = useMutation<
    IUserGroupEnableMutation,
    IUserGroupEnableMutationVariables
  >(USER_GROUP_ENABLE);
  const [enableUser] = useMutation<
    IUserEnableMutation,
    IUserEnableMutationVariables
  >(USER_ENABLE);

  const mutation = useCallback(async () => {
    let errors: IError[] = [];
    let message = '';
    if (buttonType === 'disableButton') {
      // handled as special case in DisableUserModal
      assertCondition(entity.__typename !== 'User');
      if (entity.__typename === 'UserGroup') {
        const { data } = await disableGroup({
          variables: { input: { groupId: entity.id } },
        });
        errors = data?.userGroupDisable?.errors ?? [];
      } else if (entity.__typename === 'Role') {
        const { data } = await disableRole({
          variables: { input: { roleId: entity.id } },
        });
        errors = data?.roleDisable?.errors ?? [];
      } else {
        throw new UnreachableCaseError(entity);
      }
      message = entity.name + ' has been disabled.';
    } else {
      if (entity.__typename === 'User') {
        const { data } = await enableUser({
          variables: { input: { userId: entity.id } },
        });
        errors = data?.userEnable?.errors ?? [];
      } else if (entity.__typename === 'UserGroup') {
        const { data } = await enableGroup({
          variables: { input: { groupId: entity.id } },
        });
        errors = data?.userGroupEnable?.errors ?? [];
      } else if (entity.__typename === 'Role') {
        const { data } = await enableRole({
          variables: { input: { roleId: entity.id } },
        });
        errors = data?.roleEnable?.errors ?? [];
      } else {
        throw new UnreachableCaseError(entity);
      }

      message = entity.name + ' has been enabled.';
    }
    if (printGraphqlErrors(errors)) {
      return;
    }
    toastMessage({ message, type: 'info' });
  }, [
    buttonType,
    disableGroup,
    disableRole,
    enableGroup,
    enableRole,
    enableUser,
    entity,
  ]);
  return {
    textDictionary,
    mutation,
  };
};

export default usePermissionToggleMutation;
